import CodeIcon from '@mui/icons-material/Code';
import FolderIcon from '@mui/icons-material/Folder';
import ImageIcon from '@mui/icons-material/Image';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { Box, Grid, GridProps, Stack, Typography } from '@mui/material';
import { FileItem } from 'api/files';
import moment from 'moment';
import React from 'react';

import { formatFileSize } from '../utils/formatFileSize';

export type FileType = 'pdf' | 'image' | 'code' | 'document' | 'other';

export interface FileEntityProps {
  file: FileItem;
  variant: 'file' | 'folder';
  fileType?: FileType;
  viewMode: 'grid' | 'list';
  selected?: boolean;
  onClick?: () => void;
  onDoubleClick?: () => void;
  onContextMenu?: (e: React.MouseEvent) => void;
  focused?: boolean;
  disabled?: boolean; // New prop to indicate if the entity is disabled
}

const FileEntity: React.FC<FileEntityProps> = ({
  file,
  variant,
  fileType = 'other',
  viewMode,
  selected = false,
  focused = false,
  onClick,
  onDoubleClick,
  onContextMenu,
  disabled,
}) => {
  const { name } = file;
  const [imageError, setImageError] = React.useState(false);

  // Check if file should show thumbnail (png, jpg, webp, jpeg and size < 25MB)
  const shouldShowThumbnail = React.useMemo(() => {
    if (variant === 'folder') return false;

    const extension = name.split('.').pop()?.toLowerCase();
    const supportedExtensions = ['png', 'jpg', 'webp', 'jpeg'];
    if (!extension || !supportedExtensions.includes(extension)) return false;

    const fileSizeBytes = +file.size;
    const maxSizeBytes = 25 * 1024 * 1024; // 25MB
    if (fileSizeBytes >= maxSizeBytes) return false;

    return true;
  }, [name, file.size, variant]);

  // Reset error state when file changes
  React.useEffect(() => {
    setImageError(false);
  }, [file.url]);

  // Use the focused prop directly from parent instead of managing internal state
  // Function to get the appropriate icon based on file type
  const getFileIcon = () => {
    switch (fileType) {
      case 'pdf':
        return <PictureAsPdfIcon color="error" />;
      case 'image':
        return <ImageIcon color="primary" />;
      case 'code':
        return <CodeIcon color="success" />;
      default:
        return <InsertDriveFileOutlinedIcon color="action" />;
    }
  };

  // For grid view
  if (viewMode === 'grid') {
    return (
      <Grid item>
        <Stack
          draggable={!disabled}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: 100,
            height: 100,
            userSelect: 'none',
            m: 1,
            p: 1,
            bgcolor: selected ? 'action.selected' : 'transparent',
            border: focused ? '1px solid' : '1px solid transparent',
            borderColor: focused ? 'primary.main' : 'transparent',
            borderRadius: 1,
            ...(disabled
              ? {
                  cursor: 'not-allowed',
                  opacity: 0.5,
                }
              : {
                  cursor: 'pointer',
                  '&:hover': {
                    bgcolor: 'action.hover',
                  },
                }),
          }}
          onClick={(e) => {
            if (!disabled) {
              e.stopPropagation();
              onClick?.();
            }
          }}
          onDragStart={(e) => {
            e.dataTransfer.setData('text/uri-list', file.url);
          }}
          onDoubleClick={() => !disabled && onDoubleClick?.()}
          onContextMenu={(e) => !disabled && onContextMenu?.(e)}
        >
          <Box
            sx={{
              fontSize: 40,
              height: 50,
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
            }}
          >
            {variant === 'folder' ? (
              <FolderIcon sx={{ fontSize: 40 }} color="primary" />
            ) : shouldShowThumbnail && !imageError ? (
              <img
                src={`https://web.blackframe.app/cdn-cgi/image/w=250,quality=50,onerror=redirect/${file.url}`}
                alt={name}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  borderRadius: 4,
                }}
                onError={() => {
                  setImageError(true);
                }}
              />
            ) : (
              getFileIcon()
            )}
          </Box>
          <Typography
            variant="body2"
            align="center"
            noWrap
            sx={{
              width: '100%',
              mt: 1,
            }}
          >
            {name}
          </Typography>
        </Stack>
      </Grid>
    );
  }

  // For list view
  return (
    <Grid item xs={12}>
      <Stack
        draggable={!disabled}
        sx={{
          p: 1,
          m: 0.5,
          display: 'flex',
          userSelect: 'none',
          alignItems: 'center',
          flexDirection: 'row',
          bgcolor: selected ? 'action.selected' : 'transparent',
          border: focused ? '1px solid' : '1px solid transparent',
          borderColor: focused ? 'primary.main' : 'transparent',
          '& > *': {
            flexGrow: 1,
            flexShrink: 0,
          },
          ...(disabled
            ? {
                cursor: 'not-allowed',
                opacity: 0.5,
              }
            : {
                cursor: 'pointer',
                '&:hover': {
                  bgcolor: 'action.hover',
                },
              }),
          borderRadius: 1,
        }}
        onClick={(e) => {
          if (!disabled) {
            e.stopPropagation();
            onClick?.();
          }
        }}
        onDragStart={(e) => {
          e.dataTransfer.setData('text/uri-list', file.url);
        }}
        onDoubleClick={() => !disabled && onDoubleClick?.()}
        onContextMenu={(e) => !disabled && onContextMenu?.(e)}
      >
        <ListItemCell>
          {variant === 'folder' ? (
            <FolderIcon sx={{ mr: 2 }} color="primary" />
          ) : shouldShowThumbnail && !imageError ? (
            <Box
              sx={{
                mr: 2,
                maxHeight: 32,
                maxWidth: 32,
                minHeight: 32,
                minWidth: 32,
                overflow: 'hidden',
                borderRadius: 0.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <img
                src={`https://web.blackframe.app/cdn-cgi/image/w=250,quality=50,onerror=redirect/${file.url}`}
                alt={name}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
                onError={() => {
                  setImageError(true);
                }}
              />
            </Box>
          ) : (
            <Stack
              alignItems="center"
              justifyContent="center"
              sx={{ mr: 2, maxHeight: 32, maxWidth: 32, overflow: 'hidden' }}
            >
              {getFileIcon()}
            </Stack>
          )}
        </ListItemCell>
        <ListItemCell xs={5}>{file.name}</ListItemCell>
        <ListItemCell>{formatFileSize(+file.size)}</ListItemCell>
        <ListItemCell xs={2}>{moment(file.created_at).calendar()}</ListItemCell>
        <ListItemCell xs={2}>
          {moment(file.modified_at).calendar()}
        </ListItemCell>
        <ListItemCell>{file.status}</ListItemCell>
        {/* <Typography variant="body2" noWrap>
          {file.id}
        </Typography> */}
        {/* <Typography variant="body2" noWrap>
          {file.s3_upload_id}
        </Typography> */}
        {/* <Typography variant="body2" noWrap>
          {file.url}
        </Typography> */}
      </Stack>
    </Grid>
  );
};

export function ListItemCell(props: GridProps) {
  const { children, ...rest } = props;
  return (
    <Grid item xs={1} {...rest}>
      <Typography variant="body2">{children}</Typography>
    </Grid>
  );
}

export default FileEntity;
